import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface Props {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  // Shown while collapsed so closed sections never hide a decision
  chips?: string[];
  // Truthy when a field inside the section failed validation; bump the number
  // on every failed submit so the panel reopens even after a manual collapse
  errorSignal?: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({
  title,
  subtitle,
  icon,
  chips,
  errorSignal,
  defaultExpanded = false,
  children,
}: Props) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);

  React.useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  // A validation error must never sit inside a collapsed panel — the field
  // blocking submit would be invisible
  React.useEffect(() => {
    if (errorSignal) setExpanded(true);
  }, [errorSignal]);

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        '&::before': { display: 'none' },
        border: theme => `1px solid ${theme.palette.lightGrey.main}`,
        // MUI rounds only the first/last accordion corners by default
        borderRadius: '12px',
        '&:first-of-type': { borderRadius: '12px' },
        '&:last-of-type': { borderRadius: '12px' },
        overflow: 'hidden',
        width: '100%',
      }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          '& .MuiAccordionSummary-content': {
            my: 0,
            alignItems: 'center',
            gap: 2,
            minWidth: 0,
          },
        }}>
        {/* Stacks, not Boxes: modal.css forces MuiBox-root to 100% on mobile */}
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 44,
            height: 44,
            borderRadius: '10px',
            backgroundColor: 'accent.main',
            flexShrink: 0,
          }}>
          {icon}
        </Stack>
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="h3">{title}</Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {subtitle}
            </Typography>
          )}
        </Stack>
        {!expanded && !!chips?.length && (
          <Stack
            direction="row"
            gap={1}
            flexWrap="wrap"
            justifyContent="flex-end"
            sx={{ ml: 'auto', mr: 1, display: { xs: 'none', md: 'flex' } }}>
            {chips.map(chip => (
              <Typography
                key={chip}
                variant="caption"
                sx={{
                  backgroundColor: 'accent.main',
                  borderRadius: '999px',
                  px: 1.5,
                  py: 0.5,
                  whiteSpace: 'nowrap',
                  fontWeight: 600,
                }}>
                {chip}
              </Typography>
            ))}
          </Stack>
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={theme => ({
          // Desktop is full-bleed — the rows' own lightGrey cell borders
          // merge with the card border so the panel reads as one table.
          // Mobile inputs are standalone bordered fields, so pad instead.
          p: 2,
          borderTop: `1px solid ${theme.palette.lightGrey.main}`,
          '& div.input-group, & .inputs, & .input-row': { width: '100%' },
          // form.css orders .inputs to 1, which floats the error block to
          // the top of the titleless group — send it back below the inputs
          '& .input-group-errors': {
            order: 2,
            px: { xs: 0, md: 3 },
            pb: { xs: 0, md: 2 },
          },
          // Flush to the edges, form.css's card would double the panel outline
          [theme.breakpoints.up('md')]: {
            p: 0,
            '&& .inputs': { border: 0, borderRadius: 0 },
            '&& .inputs > *:first-child .input:first-child label': {
              borderTopLeftRadius: 0,
            },
            '&& .inputs > *:last-child .input:first-child label': {
              borderBottomLeftRadius: 0,
            },
          },
        })}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export default CollapsibleSection;
